import React from "react";
import { Icon } from "../Icon/Icon";
import { Star } from "../Icons/Star";
import { Box, BoxProps } from "../Box/Box";
import useI18n from "@I18n/hooks/useI18n";
import messages from "./messages";

export interface RatingProps extends Omit<BoxProps, "css"> {
  /** The rating value (e.g. 4.82). Displayed as "x/5" in bold. */
  rating: number;
  /**
   * Optional number of reviews. When provided, renders "(18.633 avis)"
   * next to the rating. Omit to show just the score.
   */
  reviewsCount?: number;
}

/**
 * Rating
 *
 * Displays a star icon, a bold score in the form "x.xx/5", and an optional
 * reviews count "(n avis)".
 *
 * Examples:
 *   <Rating rating={4.82} />               → ★ 4.82/5
 *   <Rating rating={4.82} reviewsCount={18633} />  → ★ 4.82/5 (18.633 avis)
 */
export const Rating = ({ rating, reviewsCount, ...boxProps }: RatingProps) => {
  const { locale, formatMessage } = useI18n();
  const formattedRating = Number.isInteger(rating)
    ? rating.toString()
    : rating.toFixed(2);
  const formattedCount =
    reviewsCount !== undefined
      ? formatMessage(messages.reviews, {
        count: reviewsCount.toLocaleString(locale),
      })
      : null;

  return (
    <Box display="inline-flex" alignItems="center" gap="1" {...boxProps}>
      <Icon
        as={Star}
        color="orange.500"
        width="18px"
        height="18px"
      />
      <Box
        as="span"
        fontWeight="bold"
        fontSize="sm"
        lineHeight="1"
        color="gray.800"
      >
        {formattedRating}/5
      </Box>
      {formattedCount && (
        <Box as="span" fontSize="sm" color="gray.700" lineHeight="1">
          {formattedCount}
        </Box>
      )}
    </Box>
  );
};
